import { Box, ButtonBase, Stack, Typography, Drawer, Slide } from "@mui/material"
import { alpha, styled } from "@mui/material/styles"
import type { FC } from ".."
import { Link, SvgIcon, ResponsiveImage, FilmFestivalLogoSimple, LocationsLogo } from ".."
import StyledContainer from "./StyledContainer"
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"
import { useRef, useEffect } from "react"

interface SplashSectionProps {
  open: boolean
  onClose: () => void
}

const SplashSection: FC<SplashSectionProps> = ({ open = true, onClose }) => {
  const isFirstRender = useRef(true)

  useEffect(() => {
    // Check if the drawer is opening for the first time
    if (!open) {
      isFirstRender.current = false
    }
  }, [open])

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      hideBackdrop={false}
      disablePortal={true}
      SlideProps={{
        appear: true,
        in: open,
        timeout: {
          enter: isFirstRender.current ? 0 : 600,
          exit: 600,
        },
        easing: {
          enter: "cubic-bezier(1, 0, 0.5, 1)",
          exit: "cubic-bezier(1, 0, 1, 1)",
        },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          backgroundColor: (theme) => theme.palette["tertiary.800"].main,
        },
      }}
    >
      <Box position="relative" overflow="hidden">
        <FullScreenVideoBackground />
      </Box>
    </Drawer>
  )
}

export default SplashSection

// A sub component for the full screen video background
const FullScreenVideoBackground: FC = () => {
  return (
    <Box
      width="100vw"
      height="100vh"
      position="relative"
      sx={{
        "& video": {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          top: 0,
          left: 0,
        },
      }}
    >
      <Box
        component="video"
        src={filmFestivalGlobals.splashSection.video}
        autoPlay
        muted
        loop
        playsInline
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        sx={{
          background: "#F05B2A",
          opacity: 0.2,
          mixBlendMode: "plus-darker",
        }}
      />
      <Box position="absolute" top={0} left={0} width="100%" height="100%" zIndex={1}>
        <TextContent />
      </Box>
    </Box>
  )
}

// A sub component for the text content
const TextContent: FC = () => {
  return (
    <StyledContainer variant="secondary" maxWidth={1400} width="100%" height="100%">
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
        gridTemplateRows={{ xs: "repeat(3, 1fr)", md: "repeat(3, 1fr)" }}
        gap={4}
        px={4}
        height="100%"
        alignItems="center"
        padding={{ xs: 4, md: 10 }}
        paddingBottom={{ xs: 15, md: 10 }}
        position="relative"
        sx={{
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Box
          position="absolute"
          left="50%"
          top="50%"
          sx={{
            transform: "translate(-50%, -50%)",
          }}
        >
          <Slide direction="down" in timeout={600} mountOnEnter unmountOnExit>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={4}
            >
              <FilmFestivalLogoSimple
                variant="secondary"
                sx={{ width: { xs: 200, md: 285 }, height: { xs: 120, md: 162 } }}
              />
              <LocationsLogo
                variant="secondary"
                sx={{ width: { xs: 90, md: 118 }, height: { xs: 40, md: 70 }, marginRight: 20 }}
              />
            </Box>
          </Slide>
        </Box>

        <Slide direction="right" in timeout={600} mountOnEnter unmountOnExit>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            gridColumn={{ xs: "1", md: "1" }}
            gridRow={{ xs: "1", md: "1" }}
            maxWidth={{ xs: "100%", md: "380px" }}
          >
            <StyledTypography
              variant="heading.xl"
              color="white"
              textAlign={{ xs: "center", md: "left" }}
              sx={{
                maxWidth: { xs: "100%", md: "200px" },
              }}
            >
              {filmFestivalGlobals.splashSection.title1}
            </StyledTypography>
            <StyledTypography
              variant="heading.xl"
              color="white"
              textAlign={{ xs: "center", md: "left" }}
              sx={{
                fontStyle: "italic",
                fontWeight: 400,
              }}
            >
              {filmFestivalGlobals.splashSection.keyword1}
            </StyledTypography>
          </Box>
        </Slide>

        <Box />

        <Slide direction="left" in timeout={600} mountOnEnter unmountOnExit>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            gridColumn={{ xs: "1", md: "3" }}
            gridRow={{ xs: "3", md: "3" }}
          >
            <StyledTypography
              variant="heading.xl"
              color="white"
              textAlign={{ xs: "center", md: "right" }}
            >
              {filmFestivalGlobals.splashSection.title2}
            </StyledTypography>
            <StyledTypography
              variant="heading.xl"
              color="white"
              textAlign={{ xs: "center", md: "right" }}
              sx={{
                fontStyle: "italic",
                fontWeight: 400,
              }}
            >
              {filmFestivalGlobals.splashSection.keyword2}
            </StyledTypography>
          </Box>
        </Slide>
      </Box>
      <Box position="absolute" top={40} right={40} display={{ xs: "none", lg: "block" }}>
        <StyledQuote variant="body.md">{filmFestivalGlobals.splashSection.quote2}</StyledQuote>
      </Box>
      <Box position="absolute" bottom={40} left={40} display={{ xs: "none", lg: "block" }}>
        <StyledQuote variant="body.md">{filmFestivalGlobals.splashSection.quote1}</StyledQuote>
      </Box>
    </StyledContainer>
  )
}

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette["tertiary.800"].main,
  fontSize: "88px",
  fontWeight: 700,
  lineHeight: "95%!important",
  letterSpacing: "-2.816px",
  [theme.breakpoints.down("lg")]: {
    fontSize: "48px",
  },
}))

const StyledQuote = styled(Typography)(({ theme }) => ({
  color: theme.palette["tertiary.800"].main,
  fontSize: "9px",
  fontWeight: 500,
  lineHeight: "90%!important",
  textTransform: "uppercase",
}))
